import { useEffect, useState } from "react";
import { Menu, CalendarDays, ChevronDown, Store, ArrowUpRight, Check, BadgeCheck, Handshake, Package, Megaphone, LayoutDashboard } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { useBooking } from "@/lib/booking";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

export function Logo({ dark = false }) {
  return (
    <button
      data-testid="logo-home-link"
      onClick={() => scrollToId("home")}
      className="flex items-center gap-3 group shrink-0"
      aria-label="Dyntra Clinics home"
    >
      <img
        src="/dyntra-logo.jpg"
        alt="Dyntra Clinics logo"
        className="h-11 w-11 rounded-full object-cover ring-2 ring-gold/60 shadow-soft transition-transform duration-500 group-hover:rotate-6"
      />
      <span className="text-left leading-none hidden sm:block">
        <span className={`block font-display text-xl font-semibold tracking-tight ${dark ? "text-ivory" : "text-pine-deep"}`}>
          Dyntra <span className="text-teal-brand italic">Clinics</span>
        </span>
        <span className="mt-1 block text-[10px] uppercase tracking-[0.3em] text-gold font-bold">
          Dental &amp; Skin Solutions
        </span>
      </span>
    </button>
  );
}

const SHORT_LABELS = {
  "Dental Care": "Dental",
  "Skin Care": "Skin",
  "Our Doctors": "Doctors",
};

const MEGA = [
  {
    title: "Why Dyntra Clinics",
    icon: BadgeCheck,
    points: ["Experienced, qualified doctors", "Patient-first consultations", "Modern, hygienic clinics", "Dental + Skin Care under one roof"],
  },
  {
    title: "Powered by Dyntra Store",
    icon: Package,
    points: ["Dental equipment ecosystem", "Procurement & technology support", "Trusted B2B healthcare platform"],
    link: { label: "Visit Dyntra Store", href: BRAND.storeUrl },
  },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setMegaOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  const mainLinks = NAV_LINKS.filter((l) => !["home", "franchise"].includes(l.id));

  return (
    <header
      data-testid="main-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || megaOpen ? "backdrop-blur-xl bg-white/90 shadow-soft border-b border-pine/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between gap-3">
          <Logo />

          <nav className="hidden lg:flex items-center gap-3 2xl:gap-4 min-w-0" aria-label="Primary">
            {mainLinks.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.id)}
                className="whitespace-nowrap text-[12px] font-semibold tracking-wide text-pine/70 hover:text-pine-deep transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-300 hover:after:w-full"
              >
                {SHORT_LABELS[l.label] || l.label}
              </button>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <button
                data-testid="nav-franchise"
                onClick={() => go("franchise")}
                className="whitespace-nowrap inline-flex items-center gap-1 rounded-full bg-gold-soft/70 px-3.5 py-1.5 text-[12px] font-bold text-pine-deep transition-all duration-300 hover:bg-gold-soft"
              >
                Franchise
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
              </button>

              <div
                data-testid="franchise-mega-menu"
                className={`absolute right-0 top-full pt-5 transition-all duration-300 ${megaOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"}`}
              >
                <div className="w-[min(760px,calc(100vw-2rem))] rounded-3xl bg-white/95 backdrop-blur-xl border border-pine/10 shadow-grand p-7" data-lenis-prevent>
                  <div className="grid grid-cols-3 gap-7">
                    {MEGA.map((col) => (
                      <div key={col.title}>
                        <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-teal-brand">
                          <col.icon className="h-4 w-4 text-gold" /> {col.title}
                        </p>
                        <ul className="mt-4 space-y-2.5">
                          {col.points.map((p) => (
                            <li key={p} className="flex items-start gap-2 text-[13px] font-medium text-slate-600">
                              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-brand" /> {p}
                            </li>
                          ))}
                        </ul>
                        {col.link && (
                          <a
                            data-testid="mega-store-link"
                            href={col.link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-pine hover:text-teal-brand transition-colors"
                          >
                            <Store className="h-3.5 w-3.5" /> {col.link.label} <ArrowUpRight className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    ))}
                    <div className="rounded-2xl bg-pine-deep p-5 text-ivory relative overflow-hidden grain">
                      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-gold">
                        <Handshake className="h-4 w-4" /> Franchise Offering
                      </p>
                      <p className="mt-3 font-display text-lg font-semibold leading-snug">Build your own Dyntra Clinic</p>
                      <ul className="mt-3 space-y-1.5 text-xs text-ivory/70">
                        <li className="flex items-center gap-1.5"><LayoutDashboard className="h-3 w-3 text-gold" /> Dyntra Connect — full access, FREE</li>
                        <li className="flex items-center gap-1.5"><Package className="h-3 w-3 text-gold" /> Flat 5% off on Dyntra Store purchases</li>
                        <li className="flex items-center gap-1.5"><BadgeCheck className="h-3 w-3 text-gold" /> Zero franchise fee, zero hidden costs</li>
                      </ul>
                      <button
                        data-testid="mega-franchise-cta"
                        onClick={() => go("franchise")}
                        className="mt-4 w-full rounded-full bg-gold px-4 py-2.5 text-xs font-bold text-pine-deep transition-all duration-300 hover:bg-gold-soft"
                      >
                        Become a Franchise Partner
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <a
              data-testid="nav-dyntra-store"
              href={BRAND.storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap hidden 2xl:inline-flex items-center gap-1.5 text-[12px] font-bold text-teal-brand hover:text-pine-deep transition-colors duration-300"
            >
              Dyntra Store <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </nav>

          <div className="flex items-center gap-2 xl:gap-3 shrink-0">
            <a
              data-testid="nav-dyntra-connect"
              href="/connect"
              className="hidden lg:inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-pine px-4 py-2 text-[12px] xl:px-5 xl:py-2.5 xl:text-[13px] font-bold text-ivory shadow-lift transition-all duration-300 hover:bg-pine-deep hover:-translate-y-0.5"
            >
              <LayoutDashboard className="h-4 w-4" /> Manage My Clinic
            </a>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" aria-label="Open menu" className="lg:hidden rounded-full p-2.5 bg-white/70 backdrop-blur border border-pine/10 text-pine-deep">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-ivory" data-lenis-prevent>
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="mt-8 flex flex-col gap-1">
                  {NAV_LINKS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`mobile-nav-${l.id}`}
                      onClick={() => go(l.id)}
                      className={`text-left px-4 py-3 rounded-xl font-semibold transition-colors ${l.id === "franchise" ? "text-pine-deep bg-gold-soft/60" : "text-pine-deep hover:bg-cream"}`}
                    >
                      {l.label}
                    </button>
                  ))}
                  <a
                    data-testid="mobile-nav-dyntra-store"
                    href={BRAND.storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-teal-brand hover:bg-cream transition-colors"
                  >
                    Dyntra Store <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <a
                    data-testid="mobile-nav-dyntra-connect"
                    href="/connect"
                    className="flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-pine-deep bg-pine/5 hover:bg-cream transition-colors"
                  >
                    <LayoutDashboard className="h-4 w-4" /> Manage My Clinic
                  </a>
                  <button
                    data-testid="mobile-nav-book-cta"
                    onClick={() => { setOpen(false); setTimeout(() => openBooking(), 250); }}
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-pine px-5 py-3.5 text-sm font-bold text-ivory"
                  >
                    <CalendarDays className="h-4 w-4" />
                    Book an Appointment
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
